import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import {
  Button,
  DialogActions,
  DialogContent,
  FormHelperText,
  MenuItem,
} from '@material-ui/core'
import { ControlledTextField } from '../form/ControlledTextField'
import { FormGroup } from '../form/FormGroup'
import { Dialog } from '../dialog/Dialog'
import { validators } from '../../validation/validators'
import { ControlledYearPicker } from '../form/ControlledYearPicker'
import { ProjectVersionFragment } from '../../generated/graphql'

export type FormValues = {
  projectId: string
  name: string
  versionName: string
  baseVersionId: string
  calendarYear: string
}
export const NO_BASE_VERSION = 'NO_BASE_VERSION'

type VersionOption = Pick<ProjectVersionFragment, 'id' | 'name'>

type Props = {
  isOpen: boolean
  defaultValues?: Partial<FormValues>
  baseVersionOptions?: VersionOption[]
  canEditBaseVersion?: boolean
  onSubmit: (values: FormValues) => void
  onClose: () => void
}

export function ProjectDialog({
  isOpen,
  defaultValues,
  baseVersionOptions,
  canEditBaseVersion,
  onSubmit,
  onClose,
}: Props): React.ReactElement {
  const { control, handleSubmit, errors, reset } = useForm<FormValues>({
    defaultValues,
  })
  const { t } = useTranslation()

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const isEditing = Boolean(defaultValues?.name)

  return (
    <Dialog
      title={defaultValues?.name ?? t('projects.createDialogTitle')}
      open={isOpen}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <FormGroup>
            <ControlledTextField
              control={control}
              name="projectId"
              rules={validators.required}
              error={errors.projectId}
              type="text"
              label={t('projects.projectId')}
            />
          </FormGroup>

          <FormGroup>
            <ControlledTextField
              control={control}
              name="name"
              rules={validators.required}
              error={errors.name}
              type="text"
              label={t('projects.name')}
            />
          </FormGroup>

          <FormGroup>
            <ControlledYearPicker
              control={control}
              name="calendarYear"
              rules={validators.required}
              error={errors.calendarYear}
              label={t('projects.calendarYear')}
            />
          </FormGroup>

          <FormGroup>
            <ControlledTextField
              control={control}
              name="versionName"
              rules={validators.required}
              error={errors.versionName}
              type="text"
              label={t('projects.versionName')}
            />
          </FormGroup>

          {canEditBaseVersion && (
            <FormGroup>
              <ControlledTextField
                control={control}
                name="baseVersionId"
                rules={validators.required}
                error={errors.baseVersionId}
                label={t('projects.baseVersion')}
                select
              >
                <MenuItem key={NO_BASE_VERSION} value={NO_BASE_VERSION}>
                  {t('projects.noBaseVersion')}
                </MenuItem>
                {baseVersionOptions?.map((baseVersion) => (
                  <MenuItem key={baseVersion.id} value={baseVersion.id}>
                    {baseVersion.name}
                  </MenuItem>
                ))}
              </ControlledTextField>
              <FormHelperText>
                {t('projects.baseVersionHelperText')}
              </FormHelperText>
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            data-cy="create-project-button"
          >
            {isEditing ? t('common.saveChanges') : t('common.create')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
